import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { format } from 'date-fns-tz'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'

import ConfirmPopup from '../../Popups/ConfirmPopup'
import { cancelBooking } from '../../../reducers/Organisation/reservationsReducer'
import { dayMinutesToHoursMinutes } from '../../../helpers/helpers'
import { getAssetLink } from '../../../helpers/get-asset-link'

const BookingOverview = ({ bookingData, timezone, cancelBooking }) => {
  const { t } = useTranslation(['client-reservations', 'common'])
  const { id, date, endTime, startTime, cancelled, refunded, chargeAmount } = bookingData
  const { image, name, multiDayBooking, checkInTime, checkOutTime, timeInterval, price } =
    bookingData.amenityId
  const endDate = bookingData.endDate ? bookingData.endDate : 0
  const [confirmPopupVisible, setConfirmPopupVisible] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const timeout = useRef(null)
  const WEEKDAYS = [
    t('client-reservations:days.sunday'),
    t('client-reservations:days.monday'),
    t('client-reservations:days.tuesday'),
    t('client-reservations:days.wednesday'),
    t('client-reservations:days.thursday'),
    t('client-reservations:days.friday'),
    t('client-reservations:days.saturday'),
  ]

  const onCancelConfirm = async () => {
    try {
      setLoading(true)
      const response = await cancelBooking(id)
      setErrorMessage(response)
      timeout.current = setTimeout(() => {
        setErrorMessage(null)
      }, 3000)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
      setConfirmPopupVisible(false)
    }
  }

  useEffect(() => {
    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [])

  return (
    <div className="amenity-overview">
      <div
        className="main-image"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${getAssetLink(
            { path: 'chitown-building', file: image },
          )})`,
        }}
      >
        {name}
      </div>
      <div className="overview-container">
        <div className="header-big">
          {t('client-reservations:bookingOverview.reservationSummary')}
        </div>
        <div className="details">
          {multiDayBooking && endDate ? (
            <React.Fragment>
              <div className="flex-space-between">
                <div>{t('client-reservations:bookingOverview.checkInTime')}</div>
                <div className="right-details">{`${
                  checkInTime !== null && checkInTime >= 0
                    ? `${dayMinutesToHoursMinutes(checkInTime)} `
                    : ''
                } ${format(date, 'MM/dd/yyyy', { timeZone: timezone })}`}</div>
              </div>
              <div className="flex-space-between">
                <div>{t('client-reservations:bookingOverview.checkOutTime')}</div>
                <div className="right-details">{`${
                  checkOutTime !== null && checkOutTime >= 0
                    ? `${dayMinutesToHoursMinutes(checkOutTime)} `
                    : ''
                } ${format(endDate, 'MM/dd/yyyy', { timeZone: timezone })}`}</div>
              </div>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <div className="flex-space-between">
                <div>{t('client-reservations:bookingOverview.date')}</div>
                <div className="right-details">{`${format(date, 'MM/dd/yyyy', {
                  timeZone: timezone,
                })}`}</div>
              </div>
              <div className="flex-space-between">
                <div>{t('client-reservations:bookingOverview.time')}</div>
                <div className="right-details">
                  {t('client-reservations:bookingOverview.timeRange', {
                    end: dayMinutesToHoursMinutes(endTime),
                    start: dayMinutesToHoursMinutes(startTime),
                  })}
                </div>
              </div>
            </React.Fragment>
          )}
          <div className="flex-space-between">
            <div>{t('client-reservations:bookingOverview.price')}</div>
            <div className="right-details">
              {!multiDayBooking && price
                ? `$${price * ((endTime - startTime) / timeInterval)}`
                : multiDayBooking
                  ? `$${chargeAmount}`
                  : t('client-reservations:bookingOverview.free')}
            </div>
          </div>
          {cancelled && (
            <div className="flex-space-between">
              <div>{t('client-reservations:bookingOverview.refund')}</div>
              <div className="right-details">
                {refunded ? t('common:booleans.yes') : t('common:booleans.no')}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="reservation-summary-footer">
        {errorMessage ? <div className="error-message">{errorMessage}</div> : null}
        <div className="reservation-button">
          <button
            disabled={cancelled}
            className="button button-primary"
            onClick={() => setConfirmPopupVisible(true)}
          >
            {t('client-reservations:bookingOverview.cancelReservation')}
          </button>
        </div>
      </div>
      <ConfirmPopup
        title={t('client-reservations:bookingOverview.cancelConfirmTitle')}
        cancelText={t('common:actions.cancel')}
        confirmText={t('client-reservations:bookingOverview.confirmSure')}
        body={t('client-reservations:bookingOverview.cancelBody', {
          date: format(date, 'MMMM dd, yyyy'),
          endDatePart: endDate
            ? t('client-reservations:bookingOverview.dateRangeEnd', {
                date: format(endDate, 'MMMM dd, yyyy'),
              })
            : '',
          name,
          timeRange: t('client-reservations:bookingOverview.timeRange', {
            end: dayMinutesToHoursMinutes(endTime),
            start: dayMinutesToHoursMinutes(startTime),
          }),
          weekday: WEEKDAYS[new Date(date).getDay()],
        })}
        isOpen={confirmPopupVisible}
        isLoading={isLoading}
        cancelCallback={() => setConfirmPopupVisible(false)}
        confirmCallback={() => onCancelConfirm()}
      />
    </div>
  )
}

BookingOverview.propTypes = {
  bookingData: PropTypes.object,
  timezone: PropTypes.string,
  cancelBooking: PropTypes.func,
}

export default connect(() => ({}), { cancelBooking })(BookingOverview)
